import { events } from "@constants/tracking";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { handleNavigateBack } from "@helpers/common";
import { isAxiosError } from "@helpers/lib/axios";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import useInvalidateQueries from "@hooks/useInvalidateQueries";
import { isWeb } from "@memorang/helpers/platform";
import type { Editor } from "@tiptap/react";
import { trackCustomEvent } from "analytics";
import { router } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { trackAnswerEvent } from "../api";
import type { TrackAnswerEventRequest } from "../api/types";
import { SESSION_EVENTS, studyResourcesLink } from "../constants";
import {
	constructExamResourcesMarkdown,
	getExitDialogConfig,
	helpDialog,
} from "../constants/dialog-configs";
import { hasListeningItems } from "../helpers/content";
import { buildSessionTimeAnswerEventRequest } from "../helpers/pause-answer-event";
import { useAilaStore } from "../hooks/useAilaStore";
import useEnqueueAssessmentOnLeave from "../hooks/useEnqueueAssessmentOnLeave";
import { usePreventBackNavigation } from "../hooks/usePreventBackNavigation";
import { useSession } from "../hooks/useSession";

const TIMEOUT_DURATION_AUTO_REDIRECT = 2000;

type Props = {
	hasFocus: boolean;
	isOlxSession?: boolean;
	isTimeExpired: boolean;
	isSummativeTest?: boolean;
	inReviewMode?: boolean;
	hasAnsweredAtLeastOneItem: boolean;
	sessionId: number;
	sessionNumericId: number;
	projectId: number;
	organizationId: number;
	examId?: string;
	examName?: string;
	currentItemId?: number;
	totalTime: number;
	isMarked?: boolean;
	showDialog?: boolean;
	numSections: number;
	numTotalItems: number;
	sessionSubType?: string;
	diagnosticLabel?: Record<string, string>;
	onToggleTimeExpiredDialog?: () => void;
	onToggleEndSessionDialog?: () => void;
	onToggleSummativeEndSessionDialog?: () => void;
	onToggleBrowserBackWarning: (value: boolean) => void;
	onToggleFlightCalculator: (value: boolean) => void;
};

const useSessionAppBarHandlers = ({
	hasFocus,
	isOlxSession,
	isTimeExpired,
	isSummativeTest,
	inReviewMode,
	hasAnsweredAtLeastOneItem,
	sessionId,
	sessionNumericId,
	projectId,
	organizationId,
	examId,
	examName,
	currentItemId,
	totalTime,
	isMarked,
	showDialog,
	numSections,
	numTotalItems,
	sessionSubType,
	diagnosticLabel,
	onToggleTimeExpiredDialog,
	onToggleEndSessionDialog,
	onToggleSummativeEndSessionDialog,
	onToggleBrowserBackWarning,
	onToggleFlightCalculator,
}: Props) => {
	const timeElapsed = useSessionStore((s) => s.timeElapsed);
	const sessionTime = useSessionStore((s) => s.sessionTime);
	const isBreakOngoing = useSessionStore((s) => s.isBreakOngoing);
	const [isPausing, setIsPausing] = useState(false);

	const { endCurrentSession } = useSession();
	const { invalidateAllDistributions } = useInvalidateQueries();
	const updateShowDrawer = useAilaStore((s) => s.updateShowDrawer);
	const updateTimeElapsed = useSessionStore((s) => s.updateTimeElapsed);
	const addSessionEvent = useSessionStore((s) => s.addSessionEvent);
	const updateMarkedItemsForReview = useSessionStore(
		(s) => s.updateMarkedItemsForReview,
	);
	const sessionType = useSessionStore((s) => s.sessionType);

	const { currentItem, currentBlock } = useCurrentSession();
	const helpDialogConfig = useAppStore((s) => s.sessionView?.helpDialog);
	const enqueueAssessmentOnLeave = useEnqueueAssessmentOnLeave();

	const constructTrackAnswerEventRequest = useCallback(
		(sessionTimeArg: number): TrackAnswerEventRequest =>
			buildSessionTimeAnswerEventRequest({
				item: currentItem,
				sessionState: useSessionStore.getState(),
				projectId,
				organizationId,
				sessionTime: sessionTimeArg,
			}),
		[currentItem, projectId, organizationId],
	);

	// Timer tick for summative sessions
	useEffect(() => {
		let interval: ReturnType<typeof setInterval> | null = null;
		interval = setInterval(() => {
			if (!inReviewMode && isSummativeTest && !isBreakOngoing && !showDialog) {
				updateTimeElapsed(timeElapsed + 1);
			}
		}, 1000);
		if (isBreakOngoing) {
			clearInterval(interval);
		}
		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [
		inReviewMode,
		isSummativeTest,
		timeElapsed,
		updateTimeElapsed,
		isBreakOngoing,
		showDialog,
	]);

	// Prevent browser unload for non-summative sessions
	useEffect(() => {
		if (!isSummativeTest && isWeb) {
			window.onbeforeunload = () => "";
			return () => {
				window.onbeforeunload = null;
			};
		}
	}, [isSummativeTest]);

	// Auto-redirect when OLX session timer expires
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional deps
	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: intentional complexity
		const handleTimeExpired = async () => {
			if (isOlxSession && isTimeExpired) {
				useSessionStore.getState().updateFinalizingSession(true);
				trackCustomEvent({ eventName: events.sessionExpired });
				const req = constructTrackAnswerEventRequest(totalTime);
				try {
					await trackAnswerEvent(req);
				} catch (error) {
					if (
						!isAxiosError(error) ||
						(error.code !== "ERR_NETWORK" && error.code !== "ERR_CANCELED")
					) {
						captureErrorToSentry(new Error("Error tracking answer event"), {
							error: serializeErrorForSentry(error),
							request: serializeErrorForSentry(req),
							...(isAxiosError(error) && {
								errorCode: error.code,
								errorMessage: error.message,
								responseStatus: error.response?.status,
							}),
						});
					}
				}
				onToggleTimeExpiredDialog?.();
				timeoutId = setTimeout(() => {
					onToggleTimeExpiredDialog?.();
					endCurrentSession({ id: sessionId });
					router.replace({
						pathname: "/(protected)/session/[id]/report",
						params: { id: sessionNumericId },
					});
				}, TIMEOUT_DURATION_AUTO_REDIRECT);
			}
		};

		handleTimeExpired();
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [
		isOlxSession,
		isTimeExpired,
		sessionId,
		sessionNumericId,
		onToggleTimeExpiredDialog,
		constructTrackAnswerEventRequest,
		totalTime,
	]);

	const handleUpdateItemStateForTime = useCallback(async () => {
		trackCustomEvent({ eventName: events.sessionPaused });
		enqueueAssessmentOnLeave(
			currentItemId,
			isSummativeTest,
			currentItem.metadata?.type,
		);
		const req = constructTrackAnswerEventRequest(
			timeElapsed + (sessionTime || 0),
		);
		try {
			await trackAnswerEvent(req);
		} catch (error) {
			if (
				!isAxiosError(error) ||
				(error.code !== "ERR_NETWORK" && error.code !== "ERR_CANCELED")
			) {
				captureErrorToSentry(new Error("Error tracking answer event"), {
					error: serializeErrorForSentry(error),
					request: serializeErrorForSentry(req),
					...(isAxiosError(error) && {
						errorCode: error.code,
						errorMessage: error.message,
						responseStatus: error.response?.status,
					}),
				});
			}
		}
		router.replace("/practice-tests");
	}, [
		constructTrackAnswerEventRequest,
		currentItem.metadata?.type,
		currentItemId,
		enqueueAssessmentOnLeave,
		isSummativeTest,
		sessionTime,
		timeElapsed,
	]);

	const handleBackPress = useCallback(
		() => onToggleBrowserBackWarning(true),
		[onToggleBrowserBackWarning],
	);

	const { allowNavigation } = usePreventBackNavigation({
		enabled: !inReviewMode,
		onBackPress: handleBackPress,
		hasFocus,
	});

	const handleCancelExit = useCallback(() => {
		onToggleBrowserBackWarning(false);
	}, [onToggleBrowserBackWarning]);

	const handleConfirmExit = useCallback(() => {
		onToggleBrowserBackWarning(false);
		allowNavigation();
		invalidateAllDistributions();
		handleNavigateBack("/(protected)/(tabs)/home");
	}, [allowNavigation, invalidateAllDistributions, onToggleBrowserBackWarning]);

	const handleNavigation = (type: "positive" | "negative") => {
		onToggleEndSessionDialog?.();
		if (type === "positive") {
			updateShowDrawer(false);
			if (hasAnsweredAtLeastOneItem) {
				useSessionStore.getState().updateFinalizingSession(true);
				endCurrentSession({ id: sessionId });
				router.replace({
					pathname: "/(protected)/session/[id]/report",
					params: { id: sessionId },
				});
			} else if (router.canGoBack()) {
				router.back();
			} else {
				router.push("/home");
			}
		}
	};

	const handleClickRightCta = () => {
		if (isSummativeTest) {
			if (inReviewMode) {
				updateShowDrawer(false);
				router.back();
			} else {
				onToggleSummativeEndSessionDialog?.();
			}
		} else {
			onToggleEndSessionDialog?.();
		}
	};

	const handleClickButton = async (type: string) => {
		if (type !== "confirm") {
			onToggleSummativeEndSessionDialog?.();
			return;
		}
		setIsPausing(true);
		invalidateAllDistributions();
		await handleUpdateItemStateForTime();
		setIsPausing(false);
		onToggleSummativeEndSessionDialog?.();
	};

	const toggleMark = () => {
		if (!inReviewMode && currentItemId) {
			if (isSummativeTest) {
				addSessionEvent(currentItemId, {
					name: SESSION_EVENTS.ITEM_MARKED_FOR_REVIEW,
					props: { marked: !isMarked },
				});
			}
			updateMarkedItemsForReview(currentItemId);
		}
	};

	const handleClickMenuItem = (type: "pause" | "help") => {
		if (type === "pause") {
			if (inReviewMode) {
				handleClickRightCta();
			} else {
				onToggleSummativeEndSessionDialog?.();
			}
		} else {
			const studyResourcesMarkdown = studyResourcesLink[examId!]
				? constructExamResourcesMarkdown(studyResourcesLink[examId!].links)
				: undefined;
			const config = helpDialog({
				sessionItems: currentBlock,
				numSections,
				examName: examName || "",
				totalTime,
				isDiagnosticTest: sessionSubType === "DIAGNOSTIC",
				numTotalQuestions: numTotalItems,
				diagnosticLabel,
				studyResourcesMarkdown,
				helpDialogConfig,
				hasListening: hasListeningItems([currentBlock]),
			});
			router.push({
				pathname: "/info",
				params: { title: config.title, body: config.body },
			});
		}
	};

	const handleToolbarActions = (
		type: "calculator" | "highlight" | "mark",
		editorRef?: Editor,
	) => {
		switch (type) {
			case "calculator":
				onToggleFlightCalculator(true);
				break;
			case "highlight":
				if (editorRef) {
					if (editorRef.isActive("highlight")) {
						editorRef.commands.unsetHighlight();
					} else {
						editorRef.commands.setHighlight();
					}
				}
				break;
			case "mark":
				toggleMark();
				break;
			default:
				break;
		}
	};

	const exitDialogCopy = sessionType
		? getExitDialogConfig({ isOlx: Boolean(isOlxSession) })[sessionType]
		: undefined;

	return {
		handleNavigation,
		handleClickRightCta,
		handleClickButton,
		handleClickMenuItem,
		handleToolbarActions,
		handleCancelExit,
		handleConfirmExit,
		exitDialogCopy,
		isPausing,
	};
};

export default useSessionAppBarHandlers;
